import { size } from '@floating-ui/dom';
import {
  autoUpdate,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS, es, fi, fr, nb, sv } from 'date-fns/locale';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import useMarqueeOverflow from '../../../../hooks/use-marquee-overflow';
import { Icon } from '../../../icon/icon';
import { NoTranslateSpan } from '../../../no-translate/no-translate';
import { Stack } from '../../../stack/stack';

// Soft reference for user information; keep optional to avoid hard dependency in UI
type UserLike = { id?: string; firstName?: string; lastName?: string; email?: string };

type Actor = UserLike | string;

type AuditMeta = {
  updatedAt: string | number | Date;
  updatedBy?: Actor;
  createdAt?: string | number | Date;
  createdBy?: Actor;
};

function toDate(d: string | number | Date) {
  return d instanceof Date ? d : new Date(d);
}

function formatExact(date: Date, locale?: string) {
  try {
    return new Intl.DateTimeFormat(locale || navigator.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

// Removed unused timeAgo helper; using date-fns with locale directly

function getActorName(actor?: Actor) {
  if (!actor) {
    return undefined;
  }
  if (typeof actor === 'string') {
    return actor;
  }
  const first = actor.firstName ?? '';
  const last = actor.lastName ?? '';
  const full = `${first} ${last}`.trim();
  return full || actor.email || actor.id || undefined;
}

type Props = {
  meta: AuditMeta;
  className?: string;
};

export const AssetTimeStamp: React.FC<Props> = ({ meta, className }) => {
  const { t, i18n } = useTranslation('skyviewAssetMenu');
  const created = React.useMemo(
    () => (meta.createdAt ? toDate(meta.createdAt) : undefined),
    [meta.createdAt],
  );
  const updated = React.useMemo(() => toDate(meta.updatedAt), [meta.updatedAt]);
  const createdByName = React.useMemo(() => getActorName(meta.createdBy), [meta.createdBy]);
  const updatedByName = React.useMemo(() => getActorName(meta.updatedBy), [meta.updatedBy]);

  const [open, setOpen] = React.useState(false);
  const [placement, setPlacement] = React.useState<'top-start' | 'bottom-start'>('bottom-start');
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [
      offset(8),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          const width = Math.round(rects.reference.width);
          elements.floating.style.minWidth = `${width}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context, { outsidePress: true, escapeKey: true });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss, role]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const el = refs.reference.current as HTMLElement | null;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const nextPlacement = rect.top > window.innerHeight * 0.85 ? 'top-start' : 'bottom-start';
    setPlacement(nextPlacement);
  }, [open, refs.reference]);

  const { isOverflowing: _isOverflowing } = useMarqueeOverflow({
    viewportRef,
    scrollerRef,
    pauseMs: 2000,
    active: !open,
    deps: [i18n.language, updated, updatedByName, createdByName],
  });

  const dfLocale = React.useMemo(() => {
    const lng = (i18n.language || 'en').split('-')[0];
    switch (lng) {
      case 'sv':
        return sv;
      case 'de':
        return de;
      case 'fi':
        return fi;
      case 'fr':
        return fr;
      case 'es':
        return es;
      case 'no':
      case 'nb':
        return nb;
      default:
        return enUS;
    }
  }, [i18n.language]);

  return (
    <Root className={className}>
      <Chip
        {...getReferenceProps({
          ref: refs.setReference,
          'aria-expanded': open,
        })}
        data-testid="asset-timestamp-chip"
      >
        <Icon icon={['fad', 'circle-info']} />
        <TextViewport ref={viewportRef}>
          <TextScroller ref={scrollerRef}>
            <Stack alignItems="center" direction="row" spacing={0.3}>
              <Label>{t('assetTimestamp.updated', { ns: 'skyviewAssetMenu' })}</Label>
              <Muted>{formatDistanceToNow(updated, { addSuffix: true, locale: dfLocale })}</Muted>
              {updatedByName ? (
                <>
                  <Muted>{t('assetTimestamp.by', { ns: 'skyviewAssetMenu' })}</Muted>
                  <Strong title={updatedByName}>{updatedByName}</Strong>
                </>
              ) : null}
            </Stack>
          </TextScroller>
        </TextViewport>
      </Chip>

      {open && (
        <FloatingPortal>
          <Panel
            {...getFloatingProps({
              ref: refs.setFloating,
              style: floatingStyles,
              tabIndex: -1,
            })}
          >
            <List>
              <Row>
                <Icon icon={['fad', 'clock']} />
                <Stack direction="column" spacing={0.1}>
                  <Stack direction="row" spacing={0.3}>
                    <Strong>
                      {t('assetTimestamp.updated', {
                        ns: 'skyviewAssetMenu',
                      })}
                    </Strong>
                    <Small as="time" dateTime={updated.toISOString()}>
                      {formatExact(updated, i18n.language)}
                    </Small>
                  </Stack>
                  {updatedByName ? (
                    <Small>
                      <NoTranslateSpan>
                        {t('assetTimestamp.by', { ns: 'skyviewAssetMenu' })} {updatedByName}
                      </NoTranslateSpan>
                    </Small>
                  ) : null}
                </Stack>
              </Row>

              <Row>
                <Icon icon={['fad', 'calendar']} />
                <Stack direction="column" spacing={0.1}>
                  <Stack direction="row" spacing={0.3}>
                    <Strong>
                      {t('assetTimestamp.created', {
                        ns: 'skyviewAssetMenu',
                      })}
                    </Strong>
                    <Small as="time" dateTime={created?.toISOString()}>
                      {created ? formatExact(created, i18n.language) : '—'}
                    </Small>
                  </Stack>
                  {createdByName ? (
                    <Small>
                      <NoTranslateSpan>
                        {t('assetTimestamp.by', { ns: 'skyviewAssetMenu' })} {createdByName}
                      </NoTranslateSpan>
                    </Small>
                  ) : null}
                </Stack>
              </Row>
            </List>
          </Panel>
        </FloatingPortal>
      )}
    </Root>
  );
};

const Root = styled.div`
  z-index: 4;
  pointer-events: none;
`;

const Chip = styled.button`
  width: 100%;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.color.gray.light};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(6px);
  padding: 0.3em 0.6em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TextViewport = styled.div`
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;
`;

const TextScroller = styled.div`
  display: inline-block;
  will-change: transform;
`;

const Panel = styled.div`
  pointer-events: auto;
  padding: 0.6em;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${({ theme }) => theme.color.gray.light};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  max-width: 26rem;
  z-index: 1000;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.6em;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 0.5em;
`;

const Label = styled.span`
  font-size: 0.7em;
  color: ${({ theme }) => theme.color.gray.dark};
`;

const Muted = styled.span`
  font-size: 0.8em;
  color: ${({ theme }) => theme.color.gray.medium};
`;

const Strong = styled.span`
  font-size: 0.7em;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray.dark};
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Small = styled.span`
  font-size: 0.7em;
  color: ${({ theme }) => theme.color.gray.dark};
`;
